import { useEffect, useMemo, useState } from 'react';

import { CheckedState } from '@radix-ui/react-checkbox';
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';

import { RootState, useAppDispatch, useAppSelector } from 'src/app/store';
import { DispatchTripRead } from 'src/common/external/bambi-api/bambiApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { Button } from 'src/common/primitives/Button';
import { IndeterminateCheckbox } from 'src/common/primitives/IndeterminateCheckbox';
import { SelectOption } from 'src/common/primitives/Select';
import { MultiSelect } from 'src/common/primitives/Select/MultiSelect';
import { useClientSideSearch } from 'src/common/util/useClientSideSearch';

import { useDispatchRepository } from '../useDispatchRepository';
import { TripListItem } from './TripListItem';
import { UnassignedTripsHeader } from './UnassignedTripsHeader';
import { ANY_DRIVER, useAssignmentOptions } from './useAssignmentOptions';
import { useDispatchToolbarActions } from './useDispatchToolbarActions';

export function UnassignedTrips() {
  const dispatch = useAppDispatch();
  const {
    selectedUnassignedTripIds,
    dispatchResponse,
    runBambiRunError,
    unassignedTripsSearchTerm,
    isMenuActionInProgress,
  } = useAppSelector((state: RootState) => state.dispatcher);
  const readState = useDispatchRepository();
  const assignmentOptions = useAssignmentOptions(
    dispatchResponse?.assignments || []
  );
  const toolBarActions = useDispatchToolbarActions();

  const { data: searchedUnassignedTrips, handleSearchTermChange } =
    useClientSideSearch<DispatchTripRead>(
      dispatchResponse?.unassigned_trips || []
    );

  const [selectedAssignments, setSelectedAssignments] = useState<
    SelectOption[]
  >([]);

  const [expandedFilters, setExpandedFilters] = useState(false);

  const handleCheckedChange = (trip: DispatchTripRead, val: CheckedState) => {
    if (val) {
      dispatch({
        type: 'dispatcher/setSelectedUnassignedTripIds',
        payload: [...selectedUnassignedTripIds, trip.id],
      });
    } else {
      dispatch({
        type: 'dispatcher/setSelectedUnassignedTripIds',
        payload: selectedUnassignedTripIds.filter((id) => id !== trip.id),
      });
    }
  };

  useEffect(() => {
    handleSearchTermChange(unassignedTripsSearchTerm);
  }, [unassignedTripsSearchTerm, handleSearchTermChange]);

  const willCallTrips = useMemo(() => {
    return searchedUnassignedTrips.filter((t) => t.is_non_activated_will_call);
  }, [searchedUnassignedTrips]);

  const otherTrips = useMemo(() => {
    return searchedUnassignedTrips.filter((t) => !t.is_non_activated_will_call);
  }, [searchedUnassignedTrips]);

  return (
    <>
      <UnassignedTripsHeader />
      {searchedUnassignedTrips.length ? (
        <div
          className={`flex items-start gap-2 text-left text-sm font-normal text-black ${
            expandedFilters ? 'border-b-2 pb-2' : ''
          }`}
        >
          <Button
            variant="ghost-icon"
            onClick={() => setExpandedFilters(!expandedFilters)}
          >
            {expandedFilters ? (
              <ChevronUpIcon className="w-[24px]" />
            ) : (
              <ChevronDownIcon className="w-[24px]" />
            )}
          </Button>
          <div className="flex flex-col gap-2">
            <label className="flex items-center  gap-x-2 text-left">
              <IndeterminateCheckbox
                onChange={(val) =>
                  toolBarActions.onSelectAll(val, searchedUnassignedTrips)
                }
                checked={
                  selectedUnassignedTripIds.length ===
                  searchedUnassignedTrips.length
                }
                indeterminate={
                  selectedUnassignedTripIds.length !==
                    searchedUnassignedTrips.length &&
                  selectedUnassignedTripIds.length > 0
                }
              />
              <div className=" flex flex-row justify-between">
                <span>Select All </span>
                <span className="text-slate-500">
                  &nbsp;({searchedUnassignedTrips.length})
                </span>
              </div>
            </label>
            {expandedFilters ? (
              <>
                <label className=" flex items-center gap-x-2 text-left">
                  <IndeterminateCheckbox
                    onChange={(update) => {
                      toolBarActions.onSelectAll(update, otherTrips);
                    }}
                    checked={otherTrips.every((t) =>
                      selectedUnassignedTripIds.find((id) => id === t.id)
                    )}
                    indeterminate={otherTrips.some((t) =>
                      selectedUnassignedTripIds.find((id) => id === t.id)
                    )}
                  />
                  <div className=" flex flex-row justify-between">
                    <span>Scheduled Trips</span>
                    <span className="text-slate-500">
                      &nbsp;({otherTrips.length})
                    </span>
                  </div>
                </label>
                <label className="flex items-center  gap-x-2 text-left">
                  <IndeterminateCheckbox
                    onChange={(update) => {
                      toolBarActions.onSelectAll(update, willCallTrips);
                    }}
                    checked={willCallTrips.every((t) =>
                      selectedUnassignedTripIds.find((id) => id === t.id)
                    )}
                    indeterminate={willCallTrips.some((t) =>
                      selectedUnassignedTripIds.find((id) => id === t.id)
                    )}
                  />
                  <div className="flex flex-row justify-between">
                    <span>Will-Call Trips</span>
                    <span className="text-slate-500">
                      &nbsp;({willCallTrips.length})
                    </span>
                  </div>
                </label>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="max-h-[75vh] flex-grow overflow-y-auto">
        {!!searchedUnassignedTrips.length ? (
          <ul
            className="flex flex-col gap-y-3"
            // This attempts to ensure we re-render without making any user facing changes
            // Mostly an experiment to see if this fixes the unassigned list and calendar
            // getting out of sync which no one has been able to recreate
            // A deeper refactor of how we manage this data is needed
            // It probably needs to be lifted to the store
            // See https://bambihealth.atlassian.net/browse/BAM-793 for more context
            data-action-in-progress={String(
              isMenuActionInProgress || readState.isFetching
            )}
          >
            {searchedUnassignedTrips.map((unassignedTrip) => (
              <li key={unassignedTrip.id}>
                <TripListItem
                  trip={unassignedTrip}
                  checked={selectedUnassignedTripIds.includes(
                    unassignedTrip.id
                  )}
                  onCheckedChange={(newChecked) => {
                    handleCheckedChange(unassignedTrip, newChecked);
                  }}
                />
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex h-full items-center justify-center text-center text-gray-500">
            There are no unassigned trips <br /> for Bambi to run :)
          </div>
        )}
      </div>
      <div
        className={`flex flex-col items-center justify-center gap-2.5 rounded-[10px] bg-white py-2 px-5 text-center font-normal text-[rgba(51,51,51,1)] drop-shadow-lg`}
      >
        {!!runBambiRunError && (
          <div className="w-full text-left">
            <FormErrorMessage>{runBambiRunError}</FormErrorMessage>
          </div>
        )}
        <div className="flex w-full items-center justify-between gap-8">
          <div className="flex flex-grow items-center gap-x-2">
            <div className="flex-grow">
              <MultiSelect
                placeholder="Any Driver"
                selected={selectedAssignments}
                options={assignmentOptions}
                onChange={(selected) => {
                  if (selected.find((i) => i.value === ANY_DRIVER)) {
                    setSelectedAssignments([]);
                  } else {
                    const uniqueSelected = (selected as SelectOption[]).reduce(
                      (prev, curr) => {
                        return {
                          ...prev,
                          [curr.value]: curr,
                        };
                      },
                      {}
                    );
                    setSelectedAssignments(Object.values(uniqueSelected));
                  }
                }}
                openUpwards
              />
            </div>
            <div className="h-0 w-5 origin-center outline outline-1 outline-[rgba(221,221,221,1)] [rotate:90deg]" />
          </div>
          <div className="flex flex-grow items-center justify-end">
            <Button
              variant="primary"
              className="w-full max-w-[15em]"
              onClick={() => {
                toolBarActions.runBambiRun({
                  tripIds: selectedUnassignedTripIds,
                  assignments: selectedAssignments.map((opt) => opt.value),
                });
              }}
            >
              Run Bambi Run
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
